import type { CSSProperties, ReactElement, ReactNode } from "react";

/**
 * The menu's shared pieces, styled by the `.mm-*` classes in `style.ts`. Every
 * tab draws its cards, headings, badges and bars from here so the eight
 * screens read as one. Thin wrappers: the look lives in the stylesheet, the
 * tab supplies the words.
 */

/** A framed block on the backdrop. `tight` shrinks the padding for lists. */
export function Card(props: {
  children: ReactNode;
  tight?: boolean;
  className?: string;
  style?: CSSProperties;
}): ReactElement {
  const cls = ["mm-card"];
  if (props.tight === true) cls.push("mm-card-tight");
  if (props.className !== undefined) cls.push(props.className);
  return (
    <section className={cls.join(" ")} style={props.style}>
      {props.children}
    </section>
  );
}

/**
 * A card's title row: small caps on the left, anything on the right (a count,
 * a button). Draw it as the first child of a `Card`.
 */
export function CardTitle(props: { children: ReactNode; aside?: ReactNode }): ReactElement {
  return (
    <div className="mm-card-title">
      <span className="mm-eyebrow">{props.children}</span>
      {props.aside === undefined ? null : <span className="mm-card-aside">{props.aside}</span>}
    </div>
  );
}

/** Small-caps label: a section name, a column head, a stat's caption. */
export function Eyebrow(props: { children: ReactNode; style?: CSSProperties }): ReactElement {
  return (
    <span className="mm-eyebrow" style={props.style}>
      {props.children}
    </span>
  );
}

/** A screen or card heading in the sans face. */
export function Title(props: {
  children: ReactNode;
  size?: "md" | "lg" | "xl";
  style?: CSSProperties;
}): ReactElement {
  return (
    <span className={`mm-title mm-title-${props.size ?? "md"}`} style={props.style}>
      {props.children}
    </span>
  );
}

/** Dimmed prose: hints, blurbs, the sentence under a control. */
export function Note(props: { children: ReactNode; style?: CSSProperties }): ReactElement {
  return (
    <span className="mm-note" style={props.style}>
      {props.children}
    </span>
  );
}

/** A number the eye should land on: mono, tabular, `tone` colours it. */
export function Num(props: {
  children: ReactNode;
  tone?: "default" | "accent" | "good" | "bad" | "dim";
  style?: CSSProperties;
}): ReactElement {
  return (
    <span className={`mm-num mm-tone-${props.tone ?? "default"}`} style={props.style}>
      {props.children}
    </span>
  );
}

/** A pill: level, tier, status. `tone` picks the colour family. */
export function Badge(props: {
  children: ReactNode;
  tone?: "accent" | "good" | "bad" | "neutral" | "warn";
  title?: string;
}): ReactElement {
  return (
    <span className={`mm-badge mm-badge-${props.tone ?? "accent"}`} title={props.title}>
      {props.children}
    </span>
  );
}

/** A progress bar. `done` turns it green. */
export function Bar(props: { fill01: number; done?: boolean; thin?: boolean }): ReactElement {
  const pct = Math.min(1, Math.max(0, props.fill01)) * 100;
  return (
    <div className={`mm-bar${props.thin === true ? " mm-bar-thin" : ""}`} aria-hidden="true">
      <div
        className={`mm-bar-fill${props.done === true ? " mm-bar-done" : ""}`}
        style={{ width: `${pct.toFixed(1)}%` }}
      />
    </div>
  );
}

/** A caption over a big number: the stat tiles on the profile. */
export function Stat(props: { label: string; value: ReactNode; sub?: ReactNode }): ReactElement {
  return (
    <div className="mm-stat">
      <span className="mm-eyebrow">{props.label}</span>
      <span className="mm-stat-value">{props.value}</span>
      {props.sub === undefined ? null : <span className="mm-note">{props.sub}</span>}
    </div>
  );
}

/** A label on the left, a value on the right. */
export function KV(props: { label: ReactNode; children: ReactNode }): ReactElement {
  return (
    <div className="mm-kv">
      <span className="mm-kv-label">{props.label}</span>
      <span className="mm-kv-value">{props.children}</span>
    </div>
  );
}

/** A key cap. */
export function Kbd(props: { children: ReactNode }): ReactElement {
  return <kbd className="mm-kbd">{props.children}</kbd>;
}

/** A hairline between two blocks. */
export function Divider(): ReactElement {
  return <div className="mm-divider" aria-hidden="true" />;
}

/**
 * Nothing here yet: an icon, a sentence, an optional way forward. Centred in
 * whatever space the list would have taken.
 */
export function EmptyState(props: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="mm-empty">
      {props.icon === undefined ? null : <span className="mm-empty-icon">{props.icon}</span>}
      <span className="mm-empty-title">{props.title}</span>
      {props.children === undefined ? null : <span className="mm-note">{props.children}</span>}
      {props.action === undefined ? null : <div className="mm-empty-action">{props.action}</div>}
    </div>
  );
}

/**
 * A modal: scrim over the whole screen, a card in the middle. `z` is the
 * stacking level (the queue uses 65, an invite 66 — see `screen.tsx`).
 */
export function Modal(props: {
  children: ReactNode;
  width?: number;
  z?: number;
  label?: string;
}): ReactElement {
  return (
    <div className="mm-scrim" style={{ zIndex: props.z }}>
      <section
        className="mm-modal"
        role="dialog"
        aria-label={props.label}
        style={{ width: props.width ?? 360 }}
      >
        {props.children}
      </section>
    </div>
  );
}

/**
 * The two-column page: a fixed sidebar and a scrolling detail pane, as the
 * traders, market, quests and hideout tabs are laid out. `side` is the
 * sidebar's width.
 */
export function SplitPage(props: {
  side: number;
  aside: ReactNode;
  children: ReactNode;
  maxWidth?: number;
}): ReactElement {
  return (
    <div className="mm-split" style={{ maxWidth: props.maxWidth ?? 1100 }}>
      <aside
        className="mm-card mm-card-tight mm-split-aside"
        style={{ flex: `0 0 ${props.side}px` }}
      >
        {props.aside}
      </aside>
      <section className="mm-card mm-split-main">{props.children}</section>
    </div>
  );
}

/**
 * A row in a sidebar list — trader, quest giver, module, category. A button,
 * selected by `aria-selected`, with an optional leading picture and trailing
 * meta.
 */
export function ListRow(props: {
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      className="mm-row"
      aria-selected={props.selected}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {props.leading === undefined ? null : <span className="mm-row-lead">{props.leading}</span>}
      <span className="mm-row-body">
        <span className="mm-row-title">{props.title}</span>
        {props.subtitle === undefined ? null : (
          <span className="mm-row-sub">{props.subtitle}</span>
        )}
      </span>
      {props.trailing === undefined ? null : <span className="mm-row-trail">{props.trailing}</span>}
    </button>
  );
}

/** A toggle switch wrapping a real checkbox, so forms and tests still see one. */
export function Switch(props: {
  checked: boolean;
  onChange: (on: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}): ReactElement {
  return (
    <label className="mm-switch">
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(ev) => {
          props.onChange(ev.target.checked);
        }}
      />
      <span className="mm-switch-track" aria-hidden="true">
        <span className="mm-switch-knob" />
      </span>
      <span className="mm-switch-label">{props.label}</span>
    </label>
  );
}
